import React, { Fragment } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import { onlyUpdateForKeys } from 'recompose';
import Link from 'components/general/Link/Link';
import LinkProps from 'utils/ts/LinkProps';

const classes = {
  item: 'list-nav__item',
  itemActive: 'list-nav__item--is-current',
};

const hasChildren = R.pipe(
  R.propOr([], 'children'),
  R.isEmpty,
  R.not,
);

const renderLink = (
  item: ListNavItemPropDef | ListNavItemChildPropDef,
  currentID?: string|number,
  onClick?: React.MouseEventHandler<HTMLAnchorElement>,
): JSX.Element => (
  <Link
    to={item.to}
    className={classNames(classes.item, {
      [classes.itemActive]: R.equals(item.id, currentID),
    })}
    onClick={onClick}
  >
    {item.title}
  </Link>
);

const ListNav: React.SFC<ListNavPropDef> = ({
  items,
  currentID,
  onItemClick,
}) => (
  <ul className="list-nav">
    {items.map(item => (
      <li
        key={`list-nav-item-${item.title}`}
        className="list-nav__section"
      >
        {item.to ? (
          <h3 className="list-nav__section-title">
            {renderLink(item, currentID, onItemClick)}
          </h3>
        ) : item.title ? (
          <h3 className="list-nav__title">
            <span>
              {item.title}
            </span>
          </h3>
        ) : null}
        <ul className="list-nav__items">
          {item.children && item.children.map(subItem => (
            <li
              key={`list-nav-item-${subItem.title}`}
              className={classNames({
                'list-nav__subgroup': hasChildren(subItem),
              })}
            >
              {hasChildren(subItem) ? (
                <Fragment>
                  <h4 className="list-nav__title">
                    <span>
                      {subItem.title}
                    </span>
                  </h4>
                  <ul className="list-nav__items">
                    {subItem.children.map(leaf => (
                      <li
                        key={`list-nav-item-${leaf.title}`}
                      >
                        {renderLink(leaf, currentID, onItemClick)}
                      </li>
                    ))}
                  </ul>
                </Fragment>
              ) : (
                renderLink(subItem, currentID, onItemClick)
              )}
            </li>
          ))}
        </ul>
      </li>
    ))}
  </ul>
);

ListNav.defaultProps = {
  items: [],
};

export interface ListNavItemChildPropDef {
  // Used to identify current item
  id: string|number;
  title: string;
  to: LinkProps | string;
  exact?: boolean;
  children?: ListNavItemChildPropDef[];
}

export interface ListNavItemPropDef {
  // Used to identify current item
  id: string|number;
  title: string;
  to?: LinkProps;
  exact?: boolean;
  children?: ListNavItemChildPropDef[];
}

export interface ListNavPropDef {
  items: ListNavItemPropDef[];
  onItemClick?();
  // ID of the currently selected navItem
  currentID?: string|number;
}

export default onlyUpdateForKeys(
  ['items', 'currentID'],
)(ListNav);
